import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";

const TR_MAP = { ı: "i", İ: "I", ş: "s", Ş: "S", ğ: "g", Ğ: "G", ç: "c", Ç: "C", ö: "o", Ö: "O", ü: "u", Ü: "U" };

const slug = (s) =>
  (s || "report")
    .replace(/[ıİşŞğĞçÇöÖüÜ]/g, (c) => TR_MAP[c])
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50) || "report";

async function toDataUrl(url) {
  const res = await fetch(url, { mode: "cors" });
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

async function fetchFontBase64(url) {
  const buf = await (await fetch(url)).arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

let fontCache = null;

// jsPDF's built-in fonts are WinAnsi only and drop Turkish glyphs (ı, ş, ğ, İ).
async function loadUnicodeFont(pdf) {
  try {
    if (!fontCache) {
      const [regular, bold] = await Promise.all([
        fetchFontBase64("/fonts/LiberationSans-Regular.ttf"),
        fetchFontBase64("/fonts/LiberationSans-Bold.ttf"),
      ]);
      fontCache = { regular, bold };
    }
    pdf.addFileToVFS("Lib-Regular.ttf", fontCache.regular);
    pdf.addFont("Lib-Regular.ttf", "Lib", "normal");
    pdf.addFileToVFS("Lib-Bold.ttf", fontCache.bold);
    pdf.addFont("Lib-Bold.ttf", "Lib", "bold");
    return "Lib";
  } catch (e) {
    console.error("Unicode font load failed, falling back to helvetica", e);
    return "helvetica";
  }
}

async function drawCover(pdf, cover) {
  const FONT = await loadUnicodeFont(pdf);
  const up = (v) => String(v || "").toLocaleUpperCase(cover.locale || "en");
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  const accent = cover.accent || "#4F46E5";

  pdf.setFillColor("#0B0F17");
  pdf.rect(0, 0, W, 150, "F");
  pdf.setFillColor(accent);
  pdf.rect(0, 150, W, 4, "F");

  let logoDrawn = false;
  if (cover.logoUrl) {
    try {
      const data = await toDataUrl(cover.logoUrl);
      const img = new Image();
      img.src = data;
      await new Promise((r) => {
        img.onload = r;
        img.onerror = r;
      });
      if (img.width) {
        const h = 40;
        const w = Math.min(190, (img.width / img.height) * h);
        pdf.setFillColor("#ffffff");
        pdf.roundedRect(M - 8, 44, w + 16, h + 16, 6, 6, "F");
        pdf.addImage(data, M, 52, w, h);
        logoDrawn = true;
      }
    } catch {
      logoDrawn = false;
    }
  }
  if (!logoDrawn) {
    pdf.setTextColor("#ffffff");
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(22);
    pdf.text(cover.brandName || "CoachMeUpp", M, 78);
  }
  pdf.setTextColor("#94a3b8");
  pdf.setFont(FONT, "normal");
  pdf.setFontSize(10);
  pdf.text(cover.kicker || "Coaching Needs Scorecard", M, 128);

  let y = 206;
  pdf.setTextColor("#0f172a");
  pdf.setFont(FONT, "bold");
  pdf.setFontSize(30);
  pdf.text(pdf.splitTextToSize(cover.companyName || "", W - M * 2), M, y);
  y += 26 * Math.max(1, pdf.splitTextToSize(cover.companyName || "", W - M * 2).length);

  pdf.setFont(FONT, "normal");
  pdf.setFontSize(11);
  pdf.setTextColor("#64748b");
  pdf.text(`${cover.subtitle || ""}  ·  ${cover.date || ""}`, M, y);
  y += 34;

  if (cover.readiness != null) {
    pdf.setFillColor("#eef2ff");
    pdf.roundedRect(M, y, 150, 68, 8, 8, "F");
    pdf.setTextColor(accent);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(9);
    pdf.text(up(cover.readinessLabel || "ORG READINESS"), M + 14, y + 22);
    pdf.setTextColor("#0f172a");
    pdf.setFontSize(28);
    pdf.text(`${cover.readiness}%`, M + 14, y + 54);
  }

  if (cover.insight) {
    const x = cover.readiness != null ? M + 166 : M;
    const w = W - x - M;
    pdf.setFillColor("#f8fafc");
    pdf.roundedRect(x, y, w, 68, 8, 8, "F");
    pdf.setFillColor(accent);
    pdf.rect(x, y, 3, 68, "F");
    pdf.setTextColor(accent);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(8);
    pdf.text(up(cover.insightLabel), x + 14, y + 18);
    pdf.setTextColor("#1f2937");
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(9.5);
    pdf.text(pdf.splitTextToSize(cover.insight, w - 28).slice(0, 4), x + 14, y + 32);
  }
  y += 100;

  const programs = (cover.programs || []).slice(0, 3);
  if (programs.length) {
    pdf.setTextColor("#64748b");
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(9);
    pdf.text(up(cover.programsLabel || "PRIORITY PROGRAMS"), M, y);
    y += 16;
    programs.forEach((p, i) => {
      pdf.setFillColor(i === 0 ? "#f1f5f9" : "#ffffff");
      pdf.roundedRect(M, y, W - M * 2, 46, 6, 6, "F");
      pdf.setDrawColor("#e2e8f0");
      pdf.roundedRect(M, y, W - M * 2, 46, 6, 6, "S");
      pdf.setTextColor(accent);
      pdf.setFont(FONT, "bold");
      pdf.setFontSize(8);
      pdf.text(up(p.tier), M + 14, y + 17);
      pdf.setTextColor("#0f172a");
      pdf.setFontSize(11);
      pdf.text(pdf.splitTextToSize(String(p.name || ""), W - M * 2 - 150)[0] || "", M + 14, y + 33);
      pdf.setTextColor("#64748b");
      pdf.setFont(FONT, "normal");
      pdf.setFontSize(9);
      pdf.text(String(p.meta || ""), W - M - 14, y + 33, { align: "right" });
      y += 54;
    });
  }

  if (cover.bookingUrl) {
    const boxY = H - 150;
    pdf.setFillColor("#0B0F17");
    pdf.roundedRect(M, boxY, W - M * 2, 96, 10, 10, "F");
    pdf.setTextColor("#ffffff");
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(13);
    pdf.text(cover.bookingTitle || "Book a call", M + 20, boxY + 34);
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(9);
    pdf.setTextColor("#94a3b8");
    pdf.text(pdf.splitTextToSize(cover.bookingNote || "", W - M * 2 - 140).slice(0, 2), M + 20, boxY + 54);
    pdf.setTextColor("#a5b4fc");
    pdf.setFontSize(8.5);
    pdf.text(pdf.splitTextToSize(cover.bookingUrl, W - M * 2 - 140)[0], M + 20, boxY + 80);
    try {
      const qr = await QRCode.toDataURL(cover.bookingUrl, { margin: 1, width: 240 });
      pdf.addImage(qr, "PNG", W - M - 92, boxY + 12, 72, 72);
    } catch {
      /* QR is a nice-to-have */
    }
  }

  pdf.setTextColor("#94a3b8");
  pdf.setFont(FONT, "normal");
  pdf.setFontSize(8);
  pdf.text(cover.footer || "", M, H - 28);
}

export async function buildReportPdf({ cover } = {}) {
  const el = document.getElementById("report-content");
  if (!el) throw new Error("report-content not found");

  const root = document.documentElement;
  root.classList.add("pdf-export");
  await new Promise((r) => setTimeout(r, 350));

  try {
    const canvas = await html2canvas(el, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: 1100,
      onclone: (doc) => doc.documentElement.classList.add("pdf-export"),
    });

    const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
    if (cover) await drawCover(pdf, cover);

    const margin = 28;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW - margin * 2;
    const ratio = imgW / canvas.width;
    const sliceH = Math.floor((pageH - margin * 2) / ratio);

    let y = 0;
    while (y < canvas.height) {
      const h = Math.min(sliceH, canvas.height - y);
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = h;
      const ctx = slice.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
      pdf.addPage();
      pdf.addImage(slice.toDataURL("image/jpeg", 0.9), "JPEG", margin, margin, imgW, h * ratio);
      y += h;
    }
    return pdf;
  } finally {
    root.classList.remove("pdf-export");
  }
}

export function reportFileName({ companyName, brandName } = {}) {
  const date = new Date().toISOString().slice(0, 10);
  return `${slug(brandName || "CoachMeUpp")}-Report-${slug(companyName)}-${date}.pdf`;
}

export async function downloadReportPdf(opts = {}) {
  const pdf = await buildReportPdf(opts);
  pdf.save(reportFileName(opts));
  return pdf;
}

export async function buildReportPdfBlob(opts = {}) {
  const pdf = await buildReportPdf(opts);
  return pdf.output("blob");
}
